import {
  Box,
  Stack,
  Flex,
  Grid,
  Heading,
  FormLabel,
  Input,
  Textarea,
  Icon,
  Image,
  useDisclosure,
  CloseButton,
  Text,
} from '@chakra-ui/react';
import { differenceInSeconds } from 'date-fns';
import { Form, Formik } from 'formik';
import { getDistance } from 'geolib';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Marker } from 'react-map-gl';
import 'react-image-lightbox/style.css';

import { IconDistance, IconLocationPinStart, IconImage, IconCamera } from 'assets/icons';
import Button from 'components/Button';
import CustomFieldBuilder from 'components/CustomFieldBuilder';
import FallbackAvatar from 'components/FallbackAvatar';
import Mapbox from 'components/Mapbox';
import JobPolygon from 'components/Mapbox/JobPolygon';
import JobRadius from 'components/Mapbox/JobRadius';
import { ConfirmationModal } from 'components/Modal';
import ActiveBreakTimer from 'containers/EmployeeTimeClock/ClockedInDetail/ActiveBreakTimer';
import BreakRulesList from 'containers/EmployeeTimeClock/ClockedInDetail/BreakRulesList';
import ClockInTimer from 'containers/EmployeeTimeClock/ClockedInDetail/ClockInTimer';
import ImageSlider from 'containers/EmployeeTimeClock/ClockedInDetail/ImageSlider';
import { initialFormValue } from 'containers/EmployeeTimeClock/schema';
import {
  detectMedia,
  formatDistance,
  getUserLocation,
  getRoundUpTime,
  getLocalTime,
} from 'containers/EmployeeTimeClock/shared/helper';
import WebCam from 'containers/EmployeeTimeClock/WebCam';
import { TIMESHEET_DISPLAY_TYPES } from 'global/options';
import { useAddBreak } from 'hooks/breaks/useAddBreak';
import { useStopBreak } from 'hooks/breaks/useStopBreak';
import { ICustomFieldItem, useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { IClockOut, useClockOut } from 'hooks/timeClock/userClockOut';
import { useTimeClockStore } from 'store/useTimeClockStore';
import { useUserObject } from 'store/useUserStore';
import {
  hasCustomFieldAccess,
  transformCustomFieldsToFormData,
  transformCustomFieldsToPostData,
  transformSavedCustomFieldsToFormData,
} from 'utils/customFieldsUtils';
import { getTimezoneOffsetInHM } from 'utils/helpers';
import toaster from 'utils/toaster';

import CABreakComplianceModal from './CABreakComplianceModal';
import { initialCABreakComplianceFormData } from './CABreakComplianceModal/schema';
import MealOrRestBreakModal from './MealOrRestBreakModal';

export interface IImageData {
  id: number;
  src: string;
  file: any;
  caption: string;
}

interface LatLng {
  latitude: number | null;
  longitude: number | null;
}

type DynamicObject = {
  [key: string]: any; // Key is a string, value can be any type
};

const ClockedInDetail: React.FC<unknown> = () => {
  const [userLocation, setUserLocation] = useState<LatLng>({
    latitude: null as number | null,
    longitude: null as number | null,
  });

  const [CAbreakComplianceFormData, setCABreakComplianceFormData] = useState<typeof initialCABreakComplianceFormData>(
    initialCABreakComplianceFormData
  );
  const [total_meal_break, setTotalMealBreak] = useState<number>(0);
  const [total_rest_break, setTotalRestBreak] = useState<number>(0);
  const [expected_meal_break, setExpectedMealBreak] = useState<number>(0);
  const [expected_rest_break, setExpectedRestBreak] = useState<number>(0);
  const [breakType, setBreakType] = useState<any>();
  const [image, setImage] = useState<any>();
  const [showSlider, setShowSlider] = useState<boolean>(false);
  const [activeBreak, setActiveBreak] = useState<any>(null);
  const [showAdditionalBreakTime, setShowAdditionalBreakTime] = useState<boolean>(false);
  const [hasMedia, setHasMedia] = useState<boolean>(false);
  const [formData, setFormData] = useState<typeof initialFormValue>(initialFormValue);
  const [mileage, setMileage] = useState<number>(0);
  const [distToJob, setDistToJob] = useState<number>(0);
  const [isMileageTracking, setIsMileageTracking] = useState<boolean>(true);
  const [isDistToJobTracking, setIsDistToJobTracking] = useState<boolean>(false);
  const [customFieldFormData, setCustomFieldFormData] = useState({});

  const webcamRef = useRef<any>(null);
  const customFieldFormikRef = useRef<any>(null);

  const {
    isOpen: showBreakOptions,
    onOpen: openBreakOptionsDialog,
    onClose: closeBreakOptionsDialog,
  } = useDisclosure();

  const { isOpen: showAddBreak, onOpen: openAddBreakDialog, onClose: closeAddBreakDialog } = useDisclosure();

  const {
    isOpen: showBreakCompliance,
    onOpen: openBreakComplianceDialog,
    onClose: closeBreakComplianceDialog,
  } = useDisclosure();

  const {
    isOpen: isClockOutDialogOpen,
    onOpen: onClockOutDialogOpen,
    onClose: onClockOutDialogClose,
  } = useDisclosure();
  const { isOpen: isWebCamOpen, onOpen: onWebCamOpen, onClose: onWebCamClose } = useDisclosure();

  const loginUser: any = useUserObject();
  const timeClockDetails: any = useTimeClockStore(state => state.timeClock);

  const { data: customFieldsData } = useFetchPaginatedCustomFields(
    {
      page: 1,
      page_size: 500,
      module: 'timesheets',
      active: '1',
      job_id: timeClockDetails?.job_id,
    },
    {
      onSuccess: (data: any) => {
        let customFields: DynamicObject = {};

        const clockOutCustomFields = getClockOutCustomFields(data?.payload);

        // Pre filled custom fields
        if (Object.prototype.toString.call(timeClockDetails?.custom_fields) === '[object Object]') {
          customFields = transformSavedCustomFieldsToFormData(clockOutCustomFields, timeClockDetails?.custom_fields);
        } else {
          // empty custom fields data

          if (clockOutCustomFields?.length) {
            customFields = transformCustomFieldsToFormData(clockOutCustomFields);
          }
        }

        setCustomFieldFormData(customFields);
      },
    }
  );

  const { status: stopBreakStatus, mutateAsync: mutateAsyncStopBreak } = useStopBreak();
  const { mutateAsync: mutateAsyncAddBreak } = useAddBreak();
  const { status, mutateAsync: mutateAsyncClockOut } = useClockOut();

  let hiddenInput: any = null;

  useEffect(() => {
    const geoLocateSuccess = (position: GeolocationPosition) => {
      const userInfo = loginUser?.userInfo;
      const companyInfo = loginUser?.companyInfo;
      const jobInfo = timeClockDetails?.jobInfo;
      const requiresJob = companyInfo?.requiresJob;
      const hasLocationTracking = userInfo?.locationTracking;
      const hasMileageTracking = userInfo?.mileageTracking;
      const shouldTrackJobMileage = jobInfo?.trackMileage;

      if (hasLocationTracking) {
        // Handle Mileage Tracking
        const canTrackMileage = hasMileageTracking && (!requiresJob || shouldTrackJobMileage);
        if (canTrackMileage) {
          setIsMileageTracking(true);
          trackMileage(position);
        }

        // Handle Distance to Job Tracking
        if (requiresJob && jobInfo) {
          setIsDistToJobTracking(true);
          trackDistFromJob(position);
        }
      }

      // Always set user location
      setUserLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    };

    if (loginUser?.userInfo?.locationTracking) {
      getUserLocation(geoLocateSuccess);
    }
    if (loginUser?.companyInfo?.faceRecognition) {
      detectMedia((hasMedia: boolean) => {
        setHasMedia(hasMedia);
      });
    }

    const data: any = {
      jobName: timeClockDetails?.jobName,
      taskName: timeClockDetails?.taskName,
      mileage: timeClockDetails?.mileage,
      notes: timeClockDetails?.notes || '',
    };
    setFormData(data);

    setExpectedBreakDuration();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (timeClockDetails && timeClockDetails?.breaks.length > 0) {
      onBreakActive(timeClockDetails?.breaks[0]);
    }
    // eslint-disable-next-line
  }, [timeClockDetails]);

  // Clock out field should show clock out + timesheet fields
  const getClockOutCustomFields = (customFieldsData: Array<ICustomFieldItem> | undefined) => {
    return Array.isArray(customFieldsData) && customFieldsData.length
      ? customFieldsData.filter((field: any) => field?.display_on !== TIMESHEET_DISPLAY_TYPES.CLOCK_IN)
      : [];
  };

  const clockOutCustomFields = useMemo(() => {
    return getClockOutCustomFields(customFieldsData?.payload);
  }, [customFieldsData]);

  const trackMileage = (position: GeolocationPosition) => {
    if (
      !position?.coords?.latitude ||
      !position?.coords?.longitude ||
      !timeClockDetails?.clockIn?.latitude ||
      !timeClockDetails?.clockIn?.longitude
    )
      return;

    const distFromClockIn = getDistance(timeClockDetails?.clockIn, {
      latitude: position?.coords?.latitude,
      longitude: position?.coords?.longitude,
    });
    const formattedDistFromClockIn = formatDistance(distFromClockIn, loginUser?.companyInfo?.milesInKm);
    setMileage(formattedDistFromClockIn);
  };

  const trackDistFromJob = (position: GeolocationPosition) => {
    const distFromJob = getDistance(
      {
        latitude: timeClockDetails?.jobInfo?.latitude,
        longitude: timeClockDetails?.jobInfo?.longitude,
      },
      {
        latitude: position?.coords.latitude,
        longitude: position?.coords.longitude,
      }
    );
    const formattedDistFromJob = formatDistance(distFromJob, loginUser?.companyInfo?.milesInKm);
    setDistToJob(formattedDistFromJob);
  };

  const fileChangeEvent = (event: any) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const img = {
      id: new Date().getTime(),
      src: URL.createObjectURL(file),
      file,
      caption: '',
    };
    setImage(img);
  };

  const onBreakSelect = (brk: any) => {
    mutateAsyncAddBreak({
      timesheet_id: timeClockDetails?.id,
      timezone: timeClockDetails?.userTimezone,
      time_break_rule_id: brk?.id,
    }).then(res => {
      onBreakActive(res?.payload);
    });
  };

  const onBreakActive = (activeBreak: any) => {
    setActiveBreak(activeBreak);
    closeBreakOptionsDialog();
    setShowAdditionalBreakTime(false);
  };

  const onBreakInActive = () => {
    setActiveBreak(null);
  };

  const onEndBreak = () => {
    mutateAsyncStopBreak({
      break_id: activeBreak?.id,
      timezone: timeClockDetails?.userTimezone,
    }).then(res => {
      const durationInMins = res.payload ? res.payload.duration_in_seconds / 60 : 0;
      const caliBreak = loginUser.assignedCaliBreakRule;

      if (caliBreak) {
        if (res?.payload.id == caliBreak.meal_break_rule_id) {
          setTotalMealBreak(total_meal_break + durationInMins);
        } else if (res?.payload.id == caliBreak.rest_break_rule_id) {
          setTotalRestBreak(total_rest_break + durationInMins);
        }
      }
      onBreakInActive();
    });
  };

  const setExpectedBreakDuration = () => {
    if (timeClockDetails) {
      setTotalMealBreak(timeClockDetails.meal_break_duration / 60.0);
      setTotalRestBreak(timeClockDetails.rest_break_duration / 60.0);
    }

    const clocked = timeClockDetails?.startLocal ? new Date(timeClockDetails?.startLocal) : null;

    if (!clocked) return;
    const localClockIn = getLocalTime(clocked, timeClockDetails?.userTimezone);

    const currentTime = new Date();

    const currentDurationInSeconds = differenceInSeconds(currentTime, localClockIn);

    const currentDurationInHours = currentDurationInSeconds / 3600;

    if (currentDurationInHours < 3.5) {
      setExpectedMealBreak(0);
      setExpectedRestBreak(0);
    } else if (currentDurationInHours <= 5) {
      setExpectedMealBreak(0);
      setExpectedRestBreak(10);
    } else if (currentDurationInHours <= 6) {
      setExpectedMealBreak(30);
      setExpectedRestBreak(10);
    } else if (currentDurationInHours <= 10) {
      setExpectedMealBreak(30);
      setExpectedRestBreak(20);
    } else if (currentDurationInHours <= 14) {
      setExpectedMealBreak(60);
      setExpectedRestBreak(30);
    } else {
      setExpectedMealBreak(60);
      setExpectedRestBreak(40);
    }
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef?.current?.getScreenshot();
    const pImage = {
      id: new Date().getTime(),
      src: imageSrc,
      caption: '',
    };
    setImage(pImage);
    onWebCamClose();
    // eslint-disable-next-line
  }, [webcamRef]);

  const onRemoveImage = () => {
    setImage(null);
  };

  const startClockOut = () => {
    if (loginUser.assignedCaliBreakRule && expected_rest_break >= 10) {
      openBreakComplianceDialog();
    } else {
      clockOut(userLocation);
    }
  };

  const onClockOut = () => {
    clockOut(userLocation);
  };

  const clockOut = (clockOutPosition?: LatLng) => {
    const hasAccess = hasCustomFieldAccess();
    const customFieldsFormValues = customFieldFormikRef?.current?.values || {};

    // @ts-ignore
    const postData: IClockOut = {
      user_id: timeClockDetails?.user_id,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      mileage: mileage,
      offset: getTimezoneOffsetInHM(),
      clock_out_time: getRoundUpTime('clock_out'),
      notes: formData?.notes,
      latitude: clockOutPosition?.latitude || null,
      longitude: clockOutPosition?.longitude || null,
      ...(hasAccess
        ? { custom_fields: transformCustomFieldsToPostData(clockOutCustomFields || [], customFieldsFormValues) }
        : {}),
    };

    if (image) {
      postData['clockout_image'] = image.file;
      postData['caption'] = image.caption;
    }

    if (CAbreakComplianceFormData.mealBreakCompliance) {
      postData['meal_break_compliance'] = CAbreakComplianceFormData.mealBreakCompliance === 'yes';
      postData['meal_break_reason'] = CAbreakComplianceFormData.mealBreakReason;
    }

    if (CAbreakComplianceFormData.restBreakCompliance) {
      postData['rest_break_compliance'] = CAbreakComplianceFormData.restBreakCompliance === 'yes';
      postData['rest_break_reason'] = CAbreakComplianceFormData.restBreakReason;
    }

    mutateAsyncClockOut(postData);
  };

  const handleSaveRestOrMealBreakSuccess = (breakDuration: number) => {
    const caliBreak = loginUser.assignedCaliBreakRule;

    if (caliBreak) {
      if (breakType == 'meal') {
        setTotalMealBreak(total_meal_break * 1.0 + breakDuration * 1.0);
      } else {
        setTotalRestBreak(total_rest_break * 1.0 + breakDuration * 1.0);
      }
    }
    closeAddBreakDialog();
  };

  const handleAddCABreak = (breakType: 'meal' | 'rest') => {
    setBreakType(breakType);
    openAddBreakDialog();
  };

  const handleCABreakComplianceFormSubmit = (values: typeof initialCABreakComplianceFormData) => {
    // Handle form submission
    setCABreakComplianceFormData(values);
    onClockOut();
  };

  const handleClockoutClick = () => {
    if (loginUser?.userInfo?.locationTracking && !userLocation.latitude && !userLocation.longitude) {
      toaster.error({ description: 'Please enable location service to clock out.' });
      return;
    }

    if (customFieldFormikRef?.current) {
      customFieldFormikRef.current.handleSubmit();

      if (customFieldFormikRef.current.submitCount === 0) {
        setTimeout(() => {
          document.getElementById('js-clockout-submit')?.click();
        }, 200);
      }

      if (customFieldFormikRef.current.isValid && customFieldFormikRef.current.submitCount >= 1) {
        onClockOutDialogOpen();
      }
    } else {
      onClockOutDialogOpen();
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isClockOutDialogOpen}
        onClose={onClockOutDialogClose}
        title="Are you sure you want to clock out? "
        subTitle="You will still be able to  clock in again!"
        confirmButtonLabel="Yes"
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: status === 'loading',
          onClick: startClockOut,
        }}
      />

      <CABreakComplianceModal
        isOpen={showBreakCompliance}
        onClose={closeBreakComplianceDialog}
        handleAddCABreak={handleAddCABreak}
        breakData={{
          expectedMealBreak: expected_meal_break,
          totalMealBreak: total_meal_break,
          expectedRestBreak: expected_rest_break,
          totalRestBreak: total_rest_break,
        }}
        handleCABreakComplianceFormSubmit={handleCABreakComplianceFormSubmit}
        isLoading={status === 'loading'}
      />

      <MealOrRestBreakModal
        isOpen={showAddBreak}
        onClose={closeAddBreakDialog}
        breakType={breakType}
        handleSaveRestOrMealBreakSuccess={handleSaveRestOrMealBreakSuccess}
      />

      <WebCam webcamRef={webcamRef} isOpen={isWebCamOpen} onClose={onWebCamClose} onCapture={capture} />

      <BreakRulesList onClose={closeBreakOptionsDialog} isOpen={showBreakOptions} onBreakSelect={onBreakSelect} />

      {showSlider && <ImageSlider image={image} mutateImage={setImage} closeSlider={() => setShowSlider(false)} />}

      <Box
        mx={{ base: 0, sm: 4, md: 6 }}
        my={{ base: 4, md: 6 }}
        p="4"
        bg="white"
        height="100%"
        borderRadius="lg"
        overflowY="auto"
        paddingBottom="40px"
      >
        <Stack direction="column">
          <Heading as="h6" size="xs" fontWeight="semibold" color="gray.500">
            Welcome {loginUser?.userInfo?.firstName}!
          </Heading>
          <Box display="flex" alignItems="baseline">
            <Heading fontSize="24px" size="md" fontWeight="semibold">
              You are currently clocked in
            </Heading>
            <Heading fontSize="20px" size="sm" fontWeight="normal" ml="2">
              {timeClockDetails ? <ClockInTimer pause={!!activeBreak} /> : '- '}
            </Heading>
          </Box>
        </Stack>

        <Box mt="6" maxWidth="1000px" mx="9px">
          <Grid templateColumns={{ base: '1fr', md: '1fr 300px' }} gap={6}>
            <Box display="flex" flexDirection="column" alignItems="center" mb="4" paddingLeft="2">
              {activeBreak ? (
                <ActiveBreakTimer
                  activeBreak={activeBreak}
                  onBreakEnd={onBreakInActive}
                  showAdditionalBreakTime={showAdditionalBreakTime}
                  setShowAdditionalBreakTime={setShowAdditionalBreakTime}
                />
              ) : (
                <Box width="100%">
                  <Box mb="4">
                    {loginUser?.companyInfo?.requiresJob && formData?.jobName && (
                      <>
                        <FormLabel>Job Name</FormLabel>
                        <Input
                          value={formData?.jobName ?? ''}
                          isReadOnly
                          background="gray.100"
                          focusBorderColor="gray.200"
                        />
                      </>
                    )}
                    {isDistToJobTracking && (
                      <Box mt="1">
                        <Icon as={IconDistance} /> Distance from job: {distToJob.toFixed(2)}{' '}
                        {loginUser?.companyInfo?.milesInKm ? 'km' : 'miles'}
                      </Box>
                    )}
                  </Box>
                  {loginUser?.companyInfo?.requiresTask &&
                    loginUser?.companyInfo?.requiresJob &&
                    formData?.taskName && (
                      <Box mb="4">
                        <FormLabel>Task Name</FormLabel>
                        <Input
                          value={formData?.taskName ?? ''}
                          isReadOnly
                          background="gray.100"
                          focusBorderColor="gray.200"
                        />
                      </Box>
                    )}
                  {isMileageTracking && (
                    <Box mb="4">
                      <FormLabel>{'Mileage ' + (loginUser?.companyInfo?.milesInKm ? '(km)' : '(miles)')}</FormLabel>
                      <Input value={mileage.toFixed(2)} isReadOnly background="gray.100" focusBorderColor="gray.200" />
                    </Box>
                  )}

                  <Box mb="4">
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                      value={formData?.notes ?? ''}
                      background="gray.100"
                      focusBorderColor="gray.200"
                      resize="none"
                      maxLength={5000}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </Box>

                  {Array.isArray(clockOutCustomFields) && clockOutCustomFields?.length ? (
                    <Formik enableReinitialize initialValues={customFieldFormData} onSubmit={() => undefined}>
                      {formikBag => {
                        customFieldFormikRef.current = formikBag;
                        return (
                          <Form noValidate>
                            <Box p="4" border="1px solid" borderColor="gray.200" borderRadius="md" mt="8">
                              <Text textTransform="uppercase" fontWeight="semibold" color="gray.600" mb="3">
                                Custom Fields
                              </Text>
                              {clockOutCustomFields.map(customField => {
                                return (
                                  <div key={customField.id}>
                                    <CustomFieldBuilder customField={customField as ICustomFieldItem} />
                                  </div>
                                );
                              })}
                            </Box>
                          </Form>
                        );
                      }}
                    </Formik>
                  ) : null}
                </Box>
              )}
            </Box>
            {loginUser?.userInfo?.locationTracking &&
            typeof userLocation.latitude === 'number' &&
            typeof userLocation.longitude === 'number' ? (
              <Box display="flex" flexDirection="column" alignItems="center" mb="4">
                <Box width="100%" height="300px" maxHeight="310px">
                  <Mapbox
                    initialViewState={{
                      latitude: userLocation.latitude,
                      longitude: userLocation.longitude,
                      zoom: 14,
                    }}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <Marker latitude={userLocation.latitude} longitude={userLocation.longitude} offset={[0, -5]}>
                      <Icon as={IconLocationPinStart} fontSize="3xl" />
                    </Marker>

                    {timeClockDetails?.jobInfo?.requireGeofence && !timeClockDetails?.jobInfo?.custom_geofence ? (
                      <JobRadius
                        radius={timeClockDetails?.jobInfo.radius}
                        latitude={timeClockDetails?.jobInfo.latitude}
                        longitude={timeClockDetails?.jobInfo.longitude}
                      />
                    ) : null}

                    {timeClockDetails?.jobInfo?.requireGeofence &&
                    timeClockDetails?.jobInfo?.custom_geofence &&
                    Array.isArray(timeClockDetails?.jobInfo?.geofence_coordinates) ? (
                      <JobPolygon coordinates={timeClockDetails?.jobInfo?.geofence_coordinates || []} />
                    ) : null}
                  </Mapbox>
                </Box>
              </Box>
            ) : null}
          </Grid>
          <Box width="100%">
            {image && (
              <Flex direction="row" wrap="wrap" mb="4">
                <Box position="relative" margin="2px">
                  <Box position="absolute" top={0} right={0} bg="secondary.400" onClick={onRemoveImage}>
                    <CloseButton />
                  </Box>

                  <Image
                    boxSize="100px"
                    height="100px"
                    objectFit="cover"
                    src={image.src}
                    alt="Image"
                    onClick={() => setShowSlider(true)}
                    fallback={<FallbackAvatar boxSize="100px" />}
                  />
                </Box>
              </Flex>
            )}
            <Flex direction="row" wrap="wrap" mb={4}>
              <Input
                id="file"
                display="none"
                type="file"
                name="employee_image"
                ref={el => (hiddenInput = el)}
                onChange={fileChangeEvent}
                accept=".jpg,.png,.jpeg"
              />
              <Button
                leftIcon={<IconImage />}
                mr="3"
                size="sm"
                color="secondary.400"
                bg="secondary.50"
                _hover={{ bg: 'secondary.100' }}
                _active={{ bg: 'secondary.100' }}
                mt={1}
                mb={1}
                onClick={() => hiddenInput.click()}
              >
                Upload Clock Out Image
              </Button>
              {loginUser?.companyInfo?.faceRecognition === 1 && hasMedia && (
                <Button
                  leftIcon={<IconCamera />}
                  size="sm"
                  color="secondary.400"
                  bg="secondary.50"
                  _hover={{ bg: 'secondary.100' }}
                  _active={{ bg: 'secondary.100' }}
                  onClick={onWebCamOpen}
                  mt={1}
                  mb={1}
                >
                  Take a Picture
                </Button>
              )}
            </Flex>
          </Box>
          <Button
            type="submit"
            id="js-clockout-submit"
            colorScheme="secondary"
            onClick={handleClockoutClick}
            disabled={!!activeBreak}
          >
            Clock Out
          </Button>
          {!!activeBreak ? (
            <Button
              bg="gray.600"
              _hover={{ bg: 'gray.700' }}
              _active={{ bg: 'gray.700' }}
              ml="5"
              onClick={onEndBreak}
              disabled={activeBreak?.break_type?.cannot_end_early && !showAdditionalBreakTime}
              isLoading={stopBreakStatus === 'loading'}
            >
              Stop Break
            </Button>
          ) : (
            <Button
              bg="gray.600"
              _hover={{ bg: 'gray.700' }}
              _active={{ bg: 'gray.700' }}
              ml="5"
              onClick={openBreakOptionsDialog}
            >
              Start Break
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};
export default ClockedInDetail;
